import React, { useCallback, useEffect, useState } from "react";
import { classes, Container, PrimaryButton } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { PlusThinStroke, Refresh } from "@bloomberg/media-ui-icons";
import { useRegUIClient } from "@foundation/reg-ui-client";
import type {
  GetUserWatchlistsResponse,
  Watchlist,
} from "@foundation/watchlist-client";
import styles from "./styles.module.css";
import { fetchPortfolioById } from "~api/portfolio/portfolioData";
import PortfolioLoadingState from "~components/Portfolio/PortfolioLoadingState";
import PortfolioTable from "~components/Portfolio/PortfolioTable";
import {
  SORT_STATES,
  LOADING_STATE,
  WATCHLIST_FAILURE_TEXT,
  MAX_WATCHLIST_LIMIT,
} from "~constants/Portfolio";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import Notification from "~components/Notification";
import WatchlistWell from "~components/WatchlistWell";
import PortfolioFormModal from "~components/Portfolio/PortfolioFormModal";
import { SetupWatchlist } from "~components/dynamic";
import WatchlistDashboard from "~components/WatchlistDashboard";
import { usePaywallAction } from "~hooks/usePaywallAction";

type EmptyPortfolioProps = {
  loadingState: string;
  onActionClick: () => void;
  onAddSymbolClick: () => void;
};

function EmptyPortfolio(props: EmptyPortfolioProps) {
  const { loadingState, onActionClick, onAddSymbolClick } = props;

  return (
    <div>
      <WatchlistWell>
        {loadingState === LOADING_STATE.FAILED && (
          <div className={styles.loadingFailed}>
            <Notification
              className={styles.failureNotification}
              text={WATCHLIST_FAILURE_TEXT}
              variant="lineBreak"
              title=""
              actionIcon={<Refresh />}
              actionText="Refresh"
              onActionClick={onActionClick}
            />
          </div>
        )}
        <Container rounded className={styles.emptyPortfolio}>
          <div className={styles.bannerText}>
            Add symbols to monitor stocks, indexes, and relevant news in one
            place.
          </div>
          <div className={styles.addSymbolContainer}>
            <PrimaryButton
              version="phoenix"
              rounded
              onClick={onAddSymbolClick}
              Icon={<PlusThinStroke />}
              iconPosition="left"
              className={styles.addSymbolButton}
            >
              <span>Add symbols</span>
            </PrimaryButton>
          </div>
        </Container>
      </WatchlistWell>
    </div>
  );
}

type PortfolioViewProps = {
  showLots: boolean;
};

export default function PortfolioView(props: PortfolioViewProps) {
  const { shouldWallAction } = usePaywallAction();
  const { showLots } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [displayLots, setDisplayLots] = useState(showLots);
  const [loadingState, setLoadingState] = useState(LOADING_STATE.EMPTY);
  const [sortState, setSortState] = useState({
    columnName: "SYMBOL",
    direction: SORT_STATES.INCREASING,
  });

  const [portfolio, setPortfolio] = useState<Watchlist | undefined>();
  const [maxWatchlistCountReached, setMaxWatchlistCountReached] =
    useState(false);

  const {
    portfolioInfos,
    selectedPortInfo,
    selectedPortfolio,
    portfolios: pagePortfolios,
    updateSelectedPortfolio,
    upsertPortfolio,
  } = usePortfolioPage();

  const { session } = useRegUIClient();
  const bconnectId = session?.userInfo?.userId ?? "";

  const isSubscriber = Boolean(session?.userInfo?.subscription);

  const refreshPortfolio = useCallback(() => {
    if (!selectedPortInfo) {
      return;
    }

    setLoadingState(LOADING_STATE.LOADING);

    fetchPortfolioById(bconnectId, selectedPortInfo.id)
      .then((result) => {
        const fetchedPortfolio = (result.data as GetUserWatchlistsResponse)
          .port;

        // update page context
        updateSelectedPortfolio(fetchedPortfolio);
        upsertPortfolio(fetchedPortfolio);

        // update local state
        setPortfolio(fetchedPortfolio);
        setLoadingState(LOADING_STATE.COMPLETE);
      })
      .catch(() => {
        // handle client error
        setLoadingState(LOADING_STATE.FAILED);
      });
  }, [selectedPortInfo, updateSelectedPortfolio, upsertPortfolio]);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleEditWatchlistClick = useCallback(() => {
    trackClickEvent("watchlistEdit", "edit", "click", "watchlist");

    setModalOpen(true);
  }, []);

  const handleToggleLotsClick = useCallback(() => {
    trackClickEvent(
      `watchlist${displayLots ? "Hide" : "Show"}LotsToggled`,
      showLots ? "hide_lots" : "show_lots",
      "click",
      "watchlist"
    );

    setDisplayLots(!displayLots);
  }, [displayLots, showLots]);

  const cycleSortState = useCallback((nextColumnName: string) => {
    trackClickEvent("watchlistSort", "sortByColumn", "click", "engagement");

    setSortState((prevSortState) => {
      const { columnName: prevColumnName, direction: prevDirection } =
        prevSortState;
      const toggledSort =
        prevDirection === SORT_STATES.DECREASING
          ? SORT_STATES.INCREASING
          : SORT_STATES.DECREASING;

      // when changing columns, use the default sort
      // else toggle between ascending and descending sort
      return {
        columnName: nextColumnName,
        direction:
          nextColumnName !== prevColumnName
            ? SORT_STATES.DECREASING
            : toggledSort,
      };
    });
  }, []);

  useEffect(() => {
    if (!selectedPortInfo) {
      // hide portfolio view when portfolio not available
      setPortfolio(undefined);

      return;
    }

    const existingPortfolio = pagePortfolios.find(
      (pagePortfolio) => pagePortfolio.portInfo.id === selectedPortInfo.id
    );

    if (existingPortfolio) {
      setPortfolio(existingPortfolio);
      updateSelectedPortfolio(existingPortfolio);
    } else {
      refreshPortfolio();
    }
  }, [selectedPortInfo?.id]);

  // to sync local data with context data for save operation
  // to refresh grid when saved
  useEffect(() => {
    if (!selectedPortfolio) {
      return;
    }

    setPortfolio(selectedPortfolio);
  }, [selectedPortfolio]);

  useEffect(() => {
    setMaxWatchlistCountReached(
      portfolioInfos.length >=
        (isSubscriber
          ? MAX_WATCHLIST_LIMIT.subscribed
          : MAX_WATCHLIST_LIMIT.registered)
    );
  }, [isSubscriber, portfolioInfos]);

  if (loadingState === LOADING_STATE.LOADING) {
    return <PortfolioLoadingState />;
  }

  if (loadingState === LOADING_STATE.FAILED) {
    return (
      <div className={styles.failureNotification}>
        <Notification
          text={WATCHLIST_FAILURE_TEXT}
          variant="lineBreak"
          title=""
          actionIcon={<Refresh />}
          actionText="Refresh"
          onActionClick={refreshPortfolio}
        />
      </div>
    );
  }

  if (!portfolio) {
    return (
      <SetupWatchlist maxWatchlistCountReached={maxWatchlistCountReached} />
    );
  }

  const { marketValue, positions } = portfolio;
  const toggleLotsText = displayLots ? "Hide Lots" : "Show Lots";

  return (
    <div
      className={classes([
        styles.portfolioView,
        shouldWallAction ? styles.mask : undefined,
      ])}
    >
      {positions.length ? (
        <>
          <WatchlistDashboard marketValue={marketValue} version={"v2"} />
          <PortfolioTable
            positions={positions}
            shouldDisplayLots={displayLots}
            sortState={sortState}
            cycleSortState={cycleSortState}
            onToggleLotsClick={handleToggleLotsClick}
            onEditClick={handleEditWatchlistClick}
            title={toggleLotsText}
          />
        </>
      ) : (
        <EmptyPortfolio
          loadingState={loadingState}
          onActionClick={refreshPortfolio}
          onAddSymbolClick={handleEditWatchlistClick}
        />
      )}
      <PortfolioFormModal
        open={modalOpen}
        onClose={handleModalClose}
        portfolioId={portfolio.portInfo.id}
        portfolioName={portfolio.portInfo.name}
      />
    </div>
  );
}
